import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Reveal, Overline } from "./Reveal";
import { useLang } from "@/i18n";
import { API, IMAGES } from "@/data";
import { Ship, CalendarDays, Route as RouteIcon, Repeat } from "lucide-react";

export const Calculator = () => {
  const { t } = useLang();
  const [routes, setRoutes] = useState([]);
  const [origin, setOrigin] = useState("montreal");
  const [dest, setDest] = useState("abidjan");

  useEffect(() => {
    axios.get(`${API}/routes`).then((r) => setRoutes(r.data)).catch(() => {});
  }, []);

  const route = useMemo(() => routes.find((r) => r.id === dest), [routes, dest]);
  const data = route ? route[origin] : null;
  const eta = data ? new Date(Date.now() + data.days * 86400000).toLocaleDateString("fr-CA") : null;

  return (
    <section id="routes" className="relative py-32 border-y border-white/10 overflow-hidden" data-testid="calculator-section">
      <div className="absolute inset-0">
        <img src={IMAGES.portAerial} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-abyss via-abyss/80 to-abyss" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <Reveal>
          <Overline>{t("calculator.overline")}</Overline>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">{t("calculator.title")}</h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Reveal className="bg-surface border border-white/10 p-8 lg:p-10 space-y-8">
            <div>
              <label className="block text-xs uppercase tracking-widest text-steel-500 mb-3">{t("calculator.origin")}</label>
              <div className="grid grid-cols-2 gap-3">
                {["montreal", "toronto"].map((o) => (
                  <button key={o} onClick={() => setOrigin(o)} data-testid={`calc-origin-${o}`}
                    className={`px-4 py-3.5 border text-sm font-semibold uppercase tracking-wide transition-all duration-300 rounded-sm ${
                      origin === o ? "bg-ember border-ember text-white" : "border-white/10 text-steel-400 hover:border-white/30"}`}>
                    {o === "montreal" ? "Montréal" : "Toronto"}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-steel-500 mb-3">{t("calculator.destination")}</label>
              <select value={dest} onChange={(e) => setDest(e.target.value)} className="input-tactical" data-testid="calc-destination-select">
                {routes.map((r) => (
                  <option key={r.id} value={r.id}>{r.port} — {r.country}</option>
                ))}
              </select>
            </div>
            <p className="text-xs text-steel-600">{t("calculator.note")}</p>
          </Reveal>

          <Reveal delay={0.15} className="bg-surface border border-white/10 p-8 lg:p-10" >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10 h-full" data-testid="calc-result">
              <div className="bg-abyss p-6 flex flex-col justify-between min-h-[140px]">
                <RouteIcon size={18} className="text-ember" />
                <div>
                  <p className="font-display text-3xl font-semibold">{data ? `${(data.nm ?? data.nm).toLocaleString("fr-FR")} nm` : "—"}</p>
                  <p className="text-xs uppercase tracking-widest text-steel-500 mt-1">{t("calculator.result.distance")} · {t("calculator.result.km")} {data ? Math.round(data.nm * 1.852).toLocaleString("fr-FR") : ""} km</p>
                </div>
              </div>
              <div className="bg-abyss p-6 flex flex-col justify-between min-h-[140px]">
                <Ship size={18} className="text-ember" />
                <div>
                  <p className="font-display text-3xl font-semibold">{data ? `${data.days} ${t("calculator.result.days")}` : "—"}</p>
                  <p className="text-xs uppercase tracking-widest text-steel-500 mt-1">{t("calculator.result.transit")}</p>
                </div>
              </div>
              <div className="bg-abyss p-6 flex flex-col justify-between min-h-[140px]">
                <CalendarDays size={18} className="text-ember" />
                <div>
                  <p className="font-display text-3xl font-semibold font-mono">{eta ?? "—"}</p>
                  <p className="text-xs uppercase tracking-widest text-steel-500 mt-1">{t("calculator.result.eta")}</p>
                </div>
              </div>
              <div className="bg-abyss p-6 flex flex-col justify-between min-h-[140px]">
                <Repeat size={18} className="text-ember" />
                <div>
                  <p className="font-display text-3xl font-semibold">1× / 7j</p>
                  <p className="text-xs uppercase tracking-widest text-steel-500 mt-1">{t("calculator.result.freq")}</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
